export { creativeWriterModelId, creativeWriterModelRevision } from "./creative-writer-model";
export { hasCachedCreativeWriterModel, removeCachedCreativeWriterModel } from "./creative-writer-cache";
export const creativeWriterLoadTimeoutMs = 180_000;
export const creativeWriterInferenceTimeoutMs = 90_000;
export const creativeWriterDirectionTimeoutMs = 30_000;

const loadingErrors: Readonly<Record<string, string>> = Object.freeze({
  "unsupported-gpu": "This writer needs a WebGPU browser and a compatible GPU. You can keep playing without LLM.",
  "storage-unavailable": "This writer needs browser model storage. Allow storage or keep playing without LLM.",
  "cache-incomplete": "Saved writer files are incomplete. Choose Retry LLM to restore them, or play without LLM.",
});

/** Bounded player guidance only; never expose arbitrary worker/runtime errors. */
export function creativeWriterSetupGuidance(error: unknown): string | null {
  return error instanceof Error && Object.values(loadingErrors).includes(error.message) ? error.message : null;
}

export interface CreativeWriterMessage {
  readonly role: "system" | "user";
  readonly content: string;
}

export interface CreativeWriterLoadOptions {
  /** Automatic restores must never repair an evicted cache by downloading files. */
  readonly cacheOnly?: boolean;
}

export interface CreativeDirectionOptions {
  /** Host eligibility: omit one label while preserving the other two model scores. */
  readonly exclude?: "1" | "2" | "3";
}

export interface CreativeWriterWorkerPort {
  postMessage(message: unknown): void;
  terminate(): void;
  addEventListener(type: "message", listener: (event: MessageEvent<unknown>) => void): void;
  addEventListener(type: "error" | "messageerror", listener: () => void): void;
}

export interface CreativeWriterDependencies {
  readonly createWorker?: () => CreativeWriterWorkerPort;
  /** Idle runtime failure has no pending promise through which to notify the host. */
  readonly onIdleFailure?: () => void;
}

interface PendingWrite {
  readonly id: number;
  readonly type: "load" | "write" | "direct";
  readonly promise: Promise<string>;
  readonly resolve: (text: string) => void;
  readonly reject: (error: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
  readonly onProgress: ((message: string) => void) | undefined;
}

export class CreativeWriterClient {
  private worker: CreativeWriterWorkerPort | null = null;
  private pending: PendingWrite | null = null;
  private loaded = false;
  private disposed = false;
  private ordinal = 0;

  constructor(private readonly dependencies: CreativeWriterDependencies = {}) {}

  get ready(): boolean { return this.loaded && !this.disposed; }

  async load(onProgress?: (message: string) => void, options?: CreativeWriterLoadOptions): Promise<void> {
    if (this.disposed) throw new Error("Creative writer is closed.");
    if (this.ready) return;
    if (this.pending?.type === "load") {
      await this.pending.promise;
      return;
    }
    if (this.worker === null) {
      const worker: CreativeWriterWorkerPort = this.dependencies.createWorker?.() ?? new Worker(
        new URL("./creative-writer.worker.ts", import.meta.url),
        { type: "module", name: "the-grind-2:creative-writer" },
      );
      this.worker = worker;
      worker.addEventListener("message", (event) => {
        if (this.worker === worker) this.receive(event.data);
      });
      const failed = () => {
        if (this.worker !== worker) return;
        const wasReadyAndIdle = this.ready && this.pending === null;
        this.fail("Creative writer stopped. Load it again to retry.");
        if (wasReadyAndIdle) {
          try { this.dependencies.onIdleFailure?.(); } catch { /* Observer only; teardown is already complete. */ }
        }
      };
      worker.addEventListener("error", failed);
      worker.addEventListener("messageerror", failed);
    }
    await this.request("load", undefined, onProgress, options?.cacheOnly === true);
  }

  async write(messages: readonly CreativeWriterMessage[]): Promise<string> {
    if (!this.ready) throw new Error("Load the creative writer before writing a story.");
    return this.request("write", this.prepareMessages(messages));
  }

  /** One model-selected presentation label, using this same loaded worker. */
  async direct(messages: readonly CreativeWriterMessage[], options?: CreativeDirectionOptions): Promise<string | null> {
    if (!this.ready) throw new Error("Load the creative writer before directing a story.");
    const exclude = options?.exclude;
    if (exclude !== undefined && exclude !== "1" && exclude !== "2" && exclude !== "3") {
      throw new Error("Creative direction exclusion must be one label.");
    }
    const choice = await this.request("direct", this.prepareMessages(messages), undefined, false, exclude);
    return choice !== exclude && (choice === "1" || choice === "2" || choice === "3") ? choice : null;
  }

  /** Two eligible public moments, selected with the existing loaded-model decision path. */
  async chooseMoment(messages: readonly CreativeWriterMessage[]): Promise<"1" | "2" | null> {
    const choice = await this.direct(messages, { exclude: "3" });
    return choice === "1" || choice === "2" ? choice : null;
  }

  private prepareMessages(messages: readonly CreativeWriterMessage[]): CreativeWriterMessage[] {
    if (!Array.isArray(messages) || messages.length < 1 || messages.length > 4
      || messages.some((message) => !message
        || (message.role !== "system" && message.role !== "user")
        || typeof message.content !== "string" || message.content.trim().length === 0
        || message.content.length > 4_000)
      || messages.reduce((length, message) => length + message.content.length, 0) > 8_000) {
      throw new Error("Creative writer needs a short story prompt.");
    }
    return messages.map(({ role, content }) => ({ role, content }));
  }

  dispose(): void {
    this.disposed = true;
    this.fail("Creative writer is closed.");
  }

  private request(
    type: "load" | "write" | "direct",
    messages?: readonly CreativeWriterMessage[],
    onProgress?: (message: string) => void,
    cacheOnly = false,
    exclude?: CreativeDirectionOptions["exclude"],
  ): Promise<string> {
    if (this.pending !== null) return Promise.reject(new Error("Creative writer is already writing."));
    const id = ++this.ordinal;
    let resolve!: (text: string) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<string>((accept, decline) => { resolve = accept; reject = decline; });
    const timer = setTimeout(() => this.fail(type === "load"
      ? "Creative writer loading timed out. Try loading it again."
      : "Creative writer took too long. Load it again to retry."),
    type === "load" ? creativeWriterLoadTimeoutMs
      : type === "direct" ? creativeWriterDirectionTimeoutMs : creativeWriterInferenceTimeoutMs);
    this.pending = { id, type, promise, resolve, reject, timer, onProgress };
    try {
      this.worker!.postMessage(type === "load"
        ? { type, id, ...(cacheOnly ? { cacheOnly: true } : {}) }
        : { type, id, messages, ...(type === "direct" && exclude !== undefined ? { exclude } : {}) });
    } catch {
      this.fail("Creative writer could not start. Load it again to retry.");
    }
    return promise;
  }

  private receive(value: unknown): void {
    if (value === null || typeof value !== "object") return;
    const response = value as Record<string, unknown>;
    const pending = this.pending;
    if (pending === null || response.id !== pending.id) return;
    if (response.type === "progress" && pending.type === "load") {
      if (typeof response.message === "string" && response.message.length <= 200) {
        // Presentation callbacks must never strand a worker request.
        try { pending.onProgress?.(response.message); } catch { /* Observer only. */ }
      }
      return;
    }
    if (response.type === "error") {
      this.fail(pending.type === "load"
        ? (typeof response.code === "string" && Object.hasOwn(loadingErrors, response.code)
          ? loadingErrors[response.code]! : "Creative writer could not load. Check your connection and retry.")
        : "Creative writer could not finish. Load it again to retry.");
      return;
    }
    if (pending.type === "load" && response.type === "ready") {
      this.loaded = true;
      this.finish("");
    } else if (pending.type === "write" && response.type === "result"
      && typeof response.text === "string" && response.text.trim().length > 0
      && response.text.length <= 4_000) {
      this.finish(response.text.trim());
    } else if (pending.type === "direct" && response.type === "direction"
      && (response.choice === null || typeof response.choice === "string")) {
      this.finish(response.choice === "1" || response.choice === "2" || response.choice === "3" ? response.choice : "");
    } else {
      this.fail("Creative writer returned an unreadable response. Load it again to retry.");
    }
  }

  private finish(text: string): void {
    const pending = this.pending;
    this.pending = null;
    if (pending !== null) { clearTimeout(pending.timer); pending.resolve(text); }
  }

  private fail(message: string): void {
    const pending = this.pending;
    this.pending = null;
    this.loaded = false;
    const worker = this.worker;
    this.worker = null;
    worker?.terminate();
    if (pending !== null) { clearTimeout(pending.timer); pending.reject(new Error(message)); }
  }
}

export function createCreativeWriterClient(dependencies?: CreativeWriterDependencies): CreativeWriterClient {
  return new CreativeWriterClient(dependencies);
}
